import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PageTransition } from '@/components/layout/PageTransition'
import { SidebarNav } from '@/components/layout/SidebarNav'
import { ConnectionBar } from '@/components/feedback/ConnectionBar'
import { navigationFor } from '@/lib/navigation'
import { useShortcuts } from '@/lib/useShortcuts'
import { Topbar } from '@/components/layout/Topbar'
import { scrimVariants } from '@/lib/motion'
import { useSession } from '@/features/auth/api/useSession'
import { usePendingPayments } from '@/features/finance/api/useFinance'
import { useUpdateLanguage } from '@/features/auth/api/useUpdateLanguage'
import { useNotifications } from '@/features/notifications/api/useNotifications'
import { ChatSocketProvider } from '@/features/chat/api/chatSocketContext'
import { useChatSocket } from '@/features/chat/api/useChatSocket'
import { useConversations } from '@/features/chat/api/useConversation'
import { useNotificationStream } from '@/features/notifications/api/useNotificationStream'
import { NotificationDrawer } from '@/features/notifications/components/NotificationDrawer'
import { useLanguage } from '@/i18n/useLanguage'

/** The application frame: persistent left navigation, sticky top chrome, and
 *  the routed page. Content is capped at the page-max width and centred, so a
 *  very wide monitor does not stretch a dense table into unreadable columns. */
export function AppShell() {
  const { t } = useTranslation()
  const { data: user } = useSession()
  // Binds the session's language preference to i18next and to `<html lang>`.
  // Mounted here because the shell wraps every routed page (FR-58).
  useLanguage()
  const updateLanguage = useUpdateLanguage()
  // The drawer closes from its own handlers — a nav link, the scrim, the Escape
  // key. Closing it from an effect keyed on the pathname would work too, but it
  // is a cascading render for something the click already knows.
  // The search box publishes its focus action here on mount and withdraws it
  // on unmount, so `/` reaches the field without this component holding a ref
  // to an element it does not render.
  const focusSearch = useRef<(() => void) | null>(null)
  const registerSearchFocus = useCallback(
    (focus: (() => void) | null) => {
      focusSearch.current = focus
    },
    [],
  )

  /* The global shortcuts (UX-DR-23). Bound in the shell rather than in the
     topbar, because `/` must work from anywhere on the page — a shortcut that
     only fires while the search box already has focus is not a shortcut.

     Ctrl/Cmd+S is deliberately not bound here: "save wherever a record is being
     edited" means the surface holding the draft owns it, and a shell-level
     handler would have nothing to save. Each editing surface calls
     useShortcuts({ onSave }) itself. */
  useShortcuts({
    onSearch: () => focusSearch.current?.(),
  })

  const [menuOpen, setMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)

  // The bell's unread count comes from the same feed the panel renders, so the
  // badge and the panel can never disagree about what is unread.
  const { data: notifications } = useNotifications()

  // The live connection, opened HERE and nowhere else (NFR-2). The shell is the
  // one component mounted for the whole session, which is what makes it the
  // right owner: a stream opened by the panel would close whenever the panel
  // did, and the bell would stop updating the moment somebody dismissed it.
  useNotificationStream()

  /* The chat socket, opened HERE for the same reason the bell's stream is: a
     socket owned by a panel closes when the panel does, so events would stop
     the moment somebody navigated away from the thread they were watching.
     The context below hands it to whatever is on screen. */
  const chatSocket = useChatSocket(Boolean(user))

  /* The chat badge: this reader's unread across every conversation (0029).
   *
   * Read HERE rather than in ChatPage, because the number has to be right for
   * somebody who is not on the chat page — that is the whole point of a badge.
   * The query is shared with the page through the same key, so opening chat
   * does not re-fetch it and marking a thread read updates both at once.
   *
   * Quotes and cases used to sit beside it as hardcoded fixtures (12 and 41)
   * that never moved and matched nothing on either page; a number that is
   * always wrong is worse than no number, so they are gone rather than frozen.
   * The plumbing stays — both nav items keep their `countKey`, and SidebarNav
   * renders no badge for a count it is not given — so whoever answers "how many
   * quotes are MINE?" (a different answer per role) can pass them here without
   * touching the nav. */
  const conversations = useConversations(Boolean(user))
  const chatUnread = (conversations.data ?? []).reduce((sum, c) => sum + c.unread, 0)

  /* The Finance badge: payments recorded and not yet confirmed, from every
   * period (QW-10, F-1). Nothing else told Finance one was waiting — the count
   * lived on a chip inside a tab nobody opens unless they already know to.
   *
   * The same query as that chip and the queue it opens, so the three cannot
   * disagree, and made only for a reader who may confirm (`usePendingPayments`
   * gates itself). Passed as `undefined` rather than `0` when the queue is
   * empty, unlike chat's: a zero on every quiet desk is noise, and SidebarNav
   * draws nothing for a count it is not given. */
  const pendingPayments = usePendingPayments().data?.length ?? 0

  if (!user) return null

  return (
    <div
      className="bg-surface-page flex h-dvh overflow-hidden"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && menuOpen) setMenuOpen(false)
      }}
    >
      <SidebarNav
        /* From the whole session, so an item gated on a capability reads the
           list the server resolved — a custom role is shown the Finance stop on
           the same terms as the seeded one. */
        sections={navigationFor(user)}
        role={user.role}
        counts={{
          chat: chatUnread,
          finance: pendingPayments > 0 ? pendingPayments : undefined,
        }}
        user={user}
        open={menuOpen}
        onNavigate={() => setMenuOpen(false)}
      />

      {/* The scrim now fades out as well as in — previously it vanished on the
          frame the menu closed, while the sidebar itself slid away, so the two
          halves of one dismissal disagreed. */}
      <AnimatePresence>
        {menuOpen ? (
          <motion.button
            type="button"
            aria-label={t('common.menu.close')}
            onClick={() => setMenuOpen(false)}
            variants={scrimVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-30 bg-[var(--overlay-scrim)] lg:hidden"
          />
        ) : null}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          user={user}
          unreadCount={notifications?.unreadCount ?? 0}
          onOpenMenu={() => setMenuOpen(true)}
          onOpenNotifications={() => setNotificationsOpen(true)}
          onLanguageChange={(language) => updateLanguage.mutate(language)}
          onRegisterSearchFocus={registerSearchFocus}
        />

        {/* Directly under the topbar and above the content, so it is visible
            without scrolling and does not displace what the user is reading.
            It renders nothing while the connection is up (UX-DR-18). */}
        <ConnectionBar />
        {/* The content column is min-w-0 so a wide dense table scrolls inside
            its own container instead of widening the page. Without it a flex
            item sizes to its CONTENT, every inner overflow-x-auto is defeated,
            and the whole page scrolls sideways on a phone — the one thing the
            responsive rules prohibit outright.

            The last class gives up an EXPANDED chat dock's width (N-06, #127):
            408px for the dock, 24px for its `right-6` offset and a 24px
            gutter, so the record reflows beside the conversation and nothing
            the reader can press sits under it. It holds exactly while a
            record page's ChatDock carries its marker (DOCK_EXPANDED_SELECTOR
            in ChatDock.tsx — a literal here because Tailwind only finds
            literals), so a minimised or closed dock, a reader the record's
            team does not contain, a loading page and every other route give
            the width straight back. Here rather than on the two record pages
            because this is where the page-max wrapper centres: padding on the
            pages would be written twice, and inside the 1440px cap it would
            waste ~210px at 1920.

            xl, not lg: between 1024 and 1279 the reservation would leave the
            record ~300px wide — a phone column under desktop layouts — so
            there the dock still floats over the corner as it did. */}
        <main className="asa-scroll min-w-0 flex-1 overflow-y-auto px-4 pt-6 pb-12 sm:px-6 xl:has-[[data-chat-dock=expanded]]:pr-[calc(var(--spacing-chat-panel)+3rem)]">
          {/* PageTransition reproduces the flex/gap of the column it replaces,
              so the wrapper is a positioning shell only. */}
          <div className="mx-auto flex w-full min-w-0 max-w-[var(--spacing-page-max)] flex-col">
            <ChatSocketProvider socket={chatSocket}>
              <PageTransition />
            </ChatSocketProvider>
          </div>
        </main>
      </div>

      <NotificationDrawer
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
    </div>
  )
}
